<template>
  <DefaultLayout>
    <div>
      <h3>{{ getScenario.self_evaluation_title }}</h3>

      <span v-html="getScenario.self_evaluation_description" />
      <br />
      <br />
      <strong>{{ getScenario.self_evaluation_tooltip }}</strong>
      <br />
      <br />
      <div class="text-center">
        <strong>
          {{ getScenario.self_evaluation_preferred_ordering_label }}
        </strong>
      </div>
      <div class="col-sm-12">
        <draggable
          @change="changed()"
          v-model="mySelfEvaluations"
          class="list-group"
          ghost-class="ghost"
          :disabled="disableSelfEvaluationDragging"
          style="user-select: none; -webkit-touch-callout: none; -webkit-user-select: none, -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none;"
          :forceFallback="true"
        >
          <transition-group type="transition" :name="'flip-list'">
            <div
              class="list-group-item py-1"
              v-for="(evaluation, index) in mySelfEvaluations"
              :key="evaluation.case"
              v-bind:style="getBackgroundColor(index)"
            >
              <div
                v-if="
                  !$store.state.main.playerIsOnlyFacilitator ||
                  $store.state.main.playerIsObserver
                "
                :style="getTextColor(index) + '; cursor: move'"
              >
                {{ getQuestion(evaluation.case) }}
              </div>
              <div v-else :style="getTextColor(index)">
                {{ getQuestion(evaluation.case) }}
              </div>
            </div>
          </transition-group>
        </draggable>
      </div>
      <div class="text-center">
        <strong>
          {{ getScenario.self_evaluation_unpreferred_ordering_label }}
        </strong>
      </div>

      <br />
      <div class="text-center">
        <strong>{{ $t("before-submit-reminder") }}</strong>
        <br />
        <br />
        <div
          v-if="
            !$store.state.main.playerIsOnlyFacilitator &&
            !$store.state.main.playerIsObserver
          "
        >
          <div v-if="!disableSubmitButton">
            <base-button
              type="primary"
              style="margin: 2px"
              v-on:click="submit()"
              >{{ submitText }}</base-button
            >
          </div>
        </div>
        <div v-else>
          <base-button
            disabled="true"
            type="primary"
            style="margin: 2px; cursor: initial; pointer-events: none"
            >{{ submitText }}</base-button
          >
        </div>
        <div
          v-if="
            disableSubmitButton &&
            !$store.getters.getIsFacilitator &&
            disableOpenButton
          "
        >
          {{ $t("pleasewait") }}
        </div>
        <div v-if="!disableOpenButton">
          <base-button type="primary" style="margin: 2px" v-on:click="open()">{{
            openText
          }}</base-button>
        </div>
      </div>

      <div class="text-center text-muted mb-4">
        <small>
          {{ $t("team-mate-progress") }}<br />
          <span class="d-inline-block mt-1">{{ joinedPlayersSummary }}</span><br />
          <font-awesome-icon icon="clock" /> = {{ $t("clock") }},
          <font-awesome-icon icon="check" /> = {{ $t("check") }}
        </small>
      </div>

      <div class="text-center">
        <b-button-group v-for="player in $store.getters.getPlayers" :key="player.id">
          <base-button disabled style="margin: 2px; cursor: initial">
            <font-awesome-icon :icon="getIcon(player.id)"></font-awesome-icon>
            {{ player.nick_name }} </base-button
          >&nbsp;
        </b-button-group>
      </div>
    </div>

    <Robot />
  </DefaultLayout>
</template>


<script>
import Vue from "vue";
import draggable from "vuedraggable";
import shuffleInplace from "fisher-yates/inplace";
import Swal from "sweetalert2";
import { mapState, mapGetters } from "vuex";
import { createSelfEvaluation } from "../services/APIService";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import Robot from "@/components/Robot.vue";

const cloneDeep = require("lodash.clonedeep");

export default {
  components: {
    draggable,
    DefaultLayout,
    Robot,
  },
  watch: {
    getSelfEvaluations: {
      handler(newData, oldData) {
        if (this.mounted) {
          // Mounted fetches self evaluations, but it's too early
          // to do this check at that phase
          this.startGameIfReady();
        }
      },
    },
    getGameOpen: {
      // Game opened, proceed if everything ok
      handler(newData, oldData) {
        if (this.mounted) {
          this.startGameIfReady();
        }
      },
    },
  },
  data: function () {
    return {
      disableSubmitButton: false,
      mySelfEvaluations: [],
      mounted: false,
    };
  },
  async created() {
    this.$store.dispatch("connectNotification");

    if (!this.$store.getters.hasThisPlayerDoneSelfEvaluation) {
      // Nope, let's create them in random order
      this.$store.getters.getCasesInCurrentLocale.forEach((aCase) => {
        const newSelfEvaluation = {
          player: this.$store.state.main.thisPlayerId,
          case: aCase.case,
        };
        this.mySelfEvaluations.push(newSelfEvaluation);
      });

      // Let's randomize the evaluations
      shuffleInplace(this.mySelfEvaluations);
      // And insert priorities
      this.fixPriorities();
    } else {
      this.mySelfEvaluations = cloneDeep(
        this.$store.getters.getThisPlayersSelfEvaluations
      );
    }
    this.$store.dispatch(
      "log",
      "Number of self evaluations: " + this.mySelfEvaluations.length
    );

    // Let's sort array based on priority
    this.mySelfEvaluations = this.mySelfEvaluations.sort(
      (a, b) => a.priority - b.priority
    );

    const dialogShown = this.checkIfCanProceed();

    // swal is non-blocking, if it's shown we don't want to continue (false=do not proceed)
    if (!dialogShown) {
      this.finishMounting();
    }
  },
  computed: {
    ...mapGetters(["getGameOpen", "getSelfEvaluations"]),
    getScenario() {
      return this.$store.getters.getScenarioInCurrentLocale;
    },
    disableOpenButton() {
      if (!this.$store.state.main.game.open) {
        return true;
      } else if (
        this.$store.state.main.playerIsOnlyFacilitator ||
        this.$store.state.main.playerIsObserver
      ) {
        return false;
      } else {
        return !this.$store.getters.hasThisPlayerDoneSelfEvaluation;
      }
    },
    submitText() {
      return !this.$store.getters.hasThisPlayerDoneSelfEvaluation
        ? this.$t("submit")
        : this.$t("submit-again");
    },
    openText() {
      if (!this.$store.getters.haveAllPlayersDoneSelfEvaluation) {
        return this.$t("players-not-ready-yet");
      } else {
        return this.$store.state.main.game.open
          ? this.$t("open")
          : this.$t("not-open-yet");
      }
    },
    disableSelfEvaluationDragging() {
      return (
        this.$store.state.main.playerIsOnlyFacilitator ||
        this.$store.state.main.game.reportingOpen ||
        this.$store.state.main.playerIsObserver
      );
    },
    joinedPlayersSummary() {
      const n = this.$store.getters.getPlayers.length;
      return n === 1
        ? this.$t("players-joined-one")
        : this.$t("players-joined-other", { n });
    },
  },
  methods: {
    finishMounting() {
      this.checkSubmitButtonStatus();
      this.startGameIfReady();
      this.mounted = true;
    },
    checkIfCanProceed() {
    },
    checkSubmitButtonStatus() {
      if (!this.$store.getters.hasThisPlayerDoneSelfEvaluation) {
        // No evaluations in database
        this.disableSubmitButton = false;
        return;
      }
      let disableSubmitButton = true;

      // Check if player has changed self evaluations
      this.$store.getters.getThisPlayersSelfEvaluations.forEach(
        (dbEvaluation) => {
          const foundEvaluation = this.mySelfEvaluations.find(
            (localEvaluation) =>
              dbEvaluation.priority === localEvaluation.priority &&
              dbEvaluation.case === localEvaluation.case
          );
          if (!foundEvaluation) {
            disableSubmitButton = false;
          }
        }
      );

      this.disableSubmitButton = disableSubmitButton;
    },
    getQuestion(caseId) {
      const theCase = this.$store.getters.getCaseByCaseId(caseId);

      if (!theCase) {
        this.$store.dispatch("log", "Case not found! Case id: " + caseId);
        return "";
      }
      return theCase.selfEvaluationQuestion;
    },
    getIcon(playerId) {
      if (
        this.$store.getters.hasSomePlayerDoneSelfEvaluation(playerId)
      ) {
        return "check";
      } else {
        return "clock";
      }
    },
    getBackgroundColor(index) {
      return "background-color: " + this.$store.state.main.colors[index];
    },
    getTextColor(index) {
      return "color: " + this.$store.state.main.textColors[index];
    },
    async open() {
      this.startGameIfReady(true);
    },
    fixPriorities() {
      this.mySelfEvaluations.forEach((evaluation, index) => {
        const priority = index + 1;

        evaluation.priority = priority;
      });
    },
    changed() {
      this.fixPriorities();
      this.checkSubmitButtonStatus();
    },
    async startGameIfReady(buttonClicked = false) {
      // start game if everything is ready

      if (!buttonClicked && this.$store.state.main.game.reportingOpen) {
        // When reporting has been opened, don't proceed with autopilot
        return;
      }

      if (
        this.$store.state.main.game.open &&
        !this.disableSubmitButton &&
        !this.$store.state.main.playerIsOnlyFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        this.$store.getters.hasThisPlayerDoneSelfEvaluation
      ) {
        // Unsubmitted changes
        Swal.fire({
          title: this.$t("dialog-title"),
          text: this.$t("unsubmitted-changes"),
          showCancelButton: true,
          confirmButtonText: this.$t("submit-changes-button"),
          cancelButtonText: this.$t("discard-changes-button"),
          allowOutsideClick: false,
        }).then((result) => {
          if (result.value) {
            this.submit();
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            this.gotoBegin();
          }
        });
      } else if (!this.disableOpenButton) {
        this.gotoBegin();
      }
    },
    async gotoBegin() {
      if (this.$router.currentRoute.path !== "/begin") {
        await this.$router.push("/begin");
      }
    },
    async submit() {
      // Send evaluation data to server
      // https://stackoverflow.com/questions/18983138/callback-after-all-asynchronous-foreach-callbacks-are-completed

      this.disableSubmitButton = true;

      for (let i = 0; i < this.mySelfEvaluations.length; i++) {
        const isLastCase = i + 1 === this.mySelfEvaluations.length;

        await createSelfEvaluation(
          this.$store.state.main.thisPlayerId,
          this.mySelfEvaluations[i].case,
          this.mySelfEvaluations[i].priority,
          isLastCase,
          this.$store.state.main.game.gameCode
        );
      }

      this.$store.dispatch("log", "Self evaluations sent.");
      // Server will send self evalution update request after the last one
    },
  },
};
</script>
<style scoped>
.ghost {
  opacity: 0.5;
  background: #ffffff;
}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
</style>

<i18n>
{
  "en": {
    "team-mate-progress": "Below you can see your teammates' progress.",
    "submit": "Submit",
    "submit-again": "Submit Again",
    "not-open-yet": "Simulation is not yet open",
    "players-not-ready-yet": "Waiting for self-evaluations",
    "open": "Go to simulation",
    "before-submit-reminder" : "This is the only question, so please consider your answers carefully before submitting.",
    "unsubmitted-changes": "You have changed the self-evaluation after submitting it. Do you want to submit the changes before opening simulation?",
    "submit-changes-button": "Yes",
    "discard-changes-button": "No",
    "dialog-title": "Self-evaluations have been changed",
    "sending": "Sending...",
    "clock": "logged in",
    "check": "completed self-evaluation",
    "pleasewait": "Please wait that your colleagues are ready and the facilitator has opened the simulation.",
    "players-joined-one": "1 player has joined.",
    "players-joined-other": "{n} players have joined."
  },
  "fi": {
    "team-mate-progress": "Näet alla, miten tiimisi jäsenet edistyvät.",
    "submit": "Valmis",
    "submit-again": "Lähetä uudestaan",
    "not-open-yet": "Simulaatio ei ole vielä auki",
    "players-not-ready-yet": "Odotetaan itsearviointeja",
    "open": "Siirry simulaatioon",
    "before-submit-reminder" : "Tämä on ainoa tehtävä, joten harkitsethan vastauksesi tarkkaan ennen lähettämistä, kiitos.",
    "unsubmitted-changes": "Olet muuttanut itsearviointia sen lähettämisen jälkeen. Haluatko lähettää itsearvioinnin uudestaan ennen simulaation avaamista?",
    "submit-changes-button": "Kyllä",
    "discard-changes-button": "Ei",
    "dialog-title": "Itsearviointia on muutettu",
    "sending": "Lähetän...",
    "clock": "kirjautunut",
    "check": "täyttänyt itsearvion",
    "pleasewait": "Odota, että muut ovat vastanneet ja pelinjohtaja avaa simulaation.",
    "players-joined-one": "1 pelaaja on liittynyt.",
    "players-joined-other": "{n} pelaajaa on liittynyt."
  },
  "sv": {
    "team-mate-progress": "Nedan kan du se din grupps framsteg.",
    "submit": "Skicka",
    "submit-again": "Skicka igen",
    "not-open-yet": "Simuleringen är ännu inte öppen",
    "players-not-ready-yet": "Väntar på självskattningar",
    "open": "Gå till simuleringen",
    "before-submit-reminder": "Detta är den enda uppgiften, så överväg noga dina svar innan du skickar in dem.",
    "unsubmitted-changes": "Du har ändrat din självskattning sedan du skickade in den. Vill du skicka in din självskattningen på nytt innan du öppnar simuleringen?",
    "submit-changes-button": "Ja",
    "discard-changes-button": "Nej",
    "dialog-title": "Självskattningen har ändrats",
    "sending": "Skickar...",
    "clock": "inloggad",
    "check": "avslutad självskattning",
    "pleasewait": "Vänta tills dina kollegor är redo och att spelledaren öppnar simuleringen.",
    "players-joined-one": "1 spelare har anslutit.",
    "players-joined-other": "{n} spelare har anslutit."
  },
 "de": {
    "team-mate-progress": "Unten können Sie die Fortschritte Ihrer Teamkollegen sehen.",
    "submit": "Fertig",
    "submit-again": "Erneut senden",
    "not-open-yet": "Die Simulation ist noch nicht offen",
    "players-not-ready-yet": "Warte auf Selbstevaluationen",
    "open": "Zur Simulation gehen",
    "before-submit-reminder": "Dies ist die einzige Frage. Bitte prüfen Sie Ihre Antworten sorgfältig, bevor Sie sie einreichen.",
    "unsubmitted-changes": "Sie haben die Selbstevaluation nach dem Absenden geändert. Möchten Sie die Änderungen vor dem Öffnen der Simulation senden?",
    "submit-changes-button": "Ja",
    "discard-changes-button": "Nein",
    "dialog-title": "Die Selbstevaluationen wurden geändert",
    "sending": "Senden...",
    "clock": "eingeloggt",
    "check": "abgeschlossene Selbstevaluation",
    "pleasewait": "Bitte warten Sie, bis Ihre Kollegen bereit sind und der Moderator die Simulation geöffnet hat.",
    "players-joined-one": "1 Teilnehmer ist beigetreten.",
    "players-joined-other": "{n} Teilnehmer sind beigetreten."
  },
  "ko": {
    "team-mate-progress": "다른 팀원들의 진행 상황을 확인해보세요.",
    "submit": "완료",
    "submit-again": "다시 제출하기",
    "not-open-yet": "시뮬레이션이 아직 시작되지 않았습니다.",
    "players-not-ready-yet": "진단 완료 대기 중...",
    "open": "시뮬레이션 시작하기",
    "before-submit-reminder" : "제출 전 응답 내용을 다시 한번 확인해주십시오. 시뮬레이션이 바로 시작됩니다.",
    "unsubmitted-changes": "진단은 여기까지 입니다. 제출하기 전 응답 내용을 다시 한번 검토해주십시오.",
    "submit-changes-button": "네",
    "discard-changes-button": "아니오",
    "dialog-title": "응답 내용이 수정되었습니다.",
    "sending": "보내는 중...",
    "clock": "접속 완료",
    "check": "진단 완료",
    "pleasewait": "모든 사람들이 진단을 완료할 때까지 잠시 기다려주십시오. 퍼실리테이터가 곧 시뮬레이션을 시작할 예정입니다.",
    "players-joined-one": "1명이 참여했습니다.",
    "players-joined-other": "{n}명이 참여했습니다."
  }
}
</i18n>
